import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Modal, ModalTitle, ModalContent, ModalFooter,
  Button,
  TextInput,
  Select, SelectOption
} from "@utahdts/utah-design-system";
import { GET_ACTIVE_SECTION_CONDITIONS } from "../../queries/siteVisitQrys/sectionConditionsQrys";
import { GET_STATUS_OPTIONS } from "../../queries/waterlinkQrys";
import { formatDate } from "../../utilities/commonFunctions/formattingFunctions";
import "./siteVisits.css";

const SectionModal = ({ section, canEdit, onClose, onSectionChange }) => {
  const { data: conditionData } = useQuery(GET_ACTIVE_SECTION_CONDITIONS);
  const { data: statusData } = useQuery(GET_STATUS_OPTIONS);
  const conditions = conditionData?.getActiveSectionConditions ?? [];
  const statusOptions = statusData?.getStatusOptions ?? [];
  const [currentSection, setCurrentSection] = useState(() => ({ ...(section || {}) }));
  const [formErrors, setFormErrors] = useState({
    description: false,
    sectionConditionId: false,
    orderNum: false,
    status: false,
  });

  useEffect(() => {
    setCurrentSection({ ...(section || {}) });
    setFormErrors({
      description: false,
      sectionConditionId: false,
      orderNum: false,
      status: false,
    });
  }, [section]);

  if (!section) return null;

  const isNew = !currentSection.sectionId;

  const updateSection = (field, value) => {
    const nextSection = {
      ...currentSection,
      [field]: value,
    };

    setCurrentSection(nextSection);
    onSectionChange?.(nextSection);
  };

  const hasFormErrors = Object.values(formErrors).some(Boolean);

  const handleSave = () => {
    const nextErrors = {
      description: !String(currentSection.description ?? "").trim(),
      sectionConditionId: !String(currentSection.sectionConditionId ?? "").trim(),
      orderNum: !String(currentSection.orderNum ?? currentSection.OrderNum ?? "").trim(),
      status: !String(currentSection.status ?? "").trim(),
    };

    setFormErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    window.alert("Implement this");
    onClose();
  };

  return (
    <div className="utah-design-system wl-modal section-modal">
      <Modal
        className="modal--medium"
        onClose={onClose}
        onEscape={onClose}
      >
        <ModalTitle>{isNew ? "New Section" : "Edit Section"}</ModalTitle>
        <ModalContent>
          <TextInput
            id="section-description"
            label="Description"
            value={currentSection.description ?? ""}
            isDisabled={!canEdit}
            isRequired
            isInvalid={formErrors.description}
            onChange={(e) => updateSection("description", e.target.value)}
          />
          <Select
            id="section-condition"
            label="Condition"
            isDisabled={!canEdit}
            isRequired
            placeholder="---"
            isInvalid={formErrors.sectionConditionId}
            value={currentSection.sectionConditionId ?? ""}
            onChange={(e) => updateSection("sectionConditionId", e.target.value)}
          >
            {conditions.map((condition) => (
              <SelectOption
                key={condition.conditionId}
                value={condition.conditionId}
                label={condition.description}
              />
            ))}
          </Select>
          <div className="modal-row">

            <div className="modal-row-item">
              <TextInput
                id="section-orderNum"
                label="Order Number"
                value={currentSection.orderNum ?? currentSection.OrderNum ?? ""}
                isDisabled={!canEdit}
                isRequired
                isInvalid={formErrors.orderNum}
                onChange={(e) => updateSection("orderNum", e.target.value)}
              />
            </div>
            <div className="modal-row-item">
              <Select
                id="section-status"
                label="Status"
                isDisabled={!canEdit}
                isRequired
                placeholder="---"
                isInvalid={formErrors.status}
                value={currentSection.status ?? ""}
                onChange={(e) => updateSection("status", e.target.value)}
              >
                {statusOptions.map((status) => (
                  <SelectOption key={status} value={status} label={status} />
                ))}
              </Select>
            </div>
          </div>
          {!isNew && (
            <div className="modal-row">
              <div className="modal-row-item">
                <strong>Last updated: </strong>
                {currentSection.lastUpdatedTs ? (
                  <>
                    {formatDate(currentSection.lastUpdatedTs)}
                    <br />by {currentSection.lastUpdatedUser}
                  </>
                ) : ("—")}
              </div>
              <div className="modal-row-item">
                <strong>Created: </strong>
                {currentSection.createdTs ? (
                  <>
                    {formatDate(currentSection.createdTs)}
                    <br />
                    by {currentSection.createdUser}
                  </>
                ) : ("—")}
              </div>
            </div>
          )}
          {hasFormErrors && (
            <div className="site-visits-save-error">
              Please provide the required fields before saving.
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button appearance="solid" color="primary" isDisabled={!canEdit} onClick={handleSave}>Save</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SectionModal;
